import styled from '@emotion/styled';
import { useContext } from 'react';

import { FeedbackTextInput } from './FeedbackTextInput';
import { LanguageSelector } from './LanguageSelector';
import { SkillOrSkillSearchAutocomplete } from './SkillOrSkillSearchAutoComplete';
import { useFeedbackDialogStore } from './use-feedback-dialog-store';
import { ApplicationContext } from '../../../shared/source/context/context';

import type { LanguageCode } from '../../../shared/source/internationalization';
import type { FunctionComponent } from 'react';

type RequestSkillChangeProps = {
    readonly descriptionValue: string;
    readonly onDescriptionChange: (input: string) => void;
};

const ContentContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
});

export const RequestSkillChange: FunctionComponent<RequestSkillChangeProps> = ({
    descriptionValue,
    onDescriptionChange,
}) => {
    const setFeedbackInformation = useFeedbackDialogStore((state) => state.setFeedbackInformation);
    const feedback = useFeedbackDialogStore((state) => state.feedback);
    const { applicationState } = useContext(ApplicationContext);

    const changeSkillLanguageLabel = useFeedbackDialogStore((state) => state.changeSkillLanguageLabel);
    const changeSkillReasonLabel = useFeedbackDialogStore((state) => state.changeSkillReasonLabel);
    const changeSkillReasonPlaceholderLabel = useFeedbackDialogStore(
        (state) => state.changeSkillReasonPlaceholderLabel,
    );

    const selectedLanguage = feedback.change.andThen((change) => change.language).unwrapOr('en-US');

    const handleLanguageChange = (language: LanguageCode): void => {
        setFeedbackInformation({
            language,
        });
    };

    return (
        <ContentContainer>
            <SkillOrSkillSearchAutocomplete currentFeedbackType="change" />
            <LanguageSelector
                availableLanguages={applicationState.availableLanguages}
                label={changeSkillLanguageLabel}
                required={true}
                selectedLanguage={selectedLanguage}
                onChange={handleLanguageChange}
            />
            <FeedbackTextInput
                placeholder={changeSkillReasonPlaceholderLabel}
                required={true}
                title={changeSkillReasonLabel}
                value={descriptionValue}
                onChange={onDescriptionChange}
            />
        </ContentContainer>
    );
};
